import { useCallback, type CSSProperties } from "react";

import { colors } from "@skilitics/designer-theme";
import {
  CancelButton,
  CompleteButton,
  Wizard,
  type ModalDialogProps,
  type WizardProps,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { useFormatCreateModuleAccessDenied } from "../../../errors/useFormatAccessDenied";
import { useFetchModuleList } from "../../routes/modules/modulePersistence";
import { useDuplicateModuleSettings } from "./DuplicateModuleSettingsContext";
import { ModuleDetailsPage } from "./pages";

type Props = ModalDialogProps &
  Pick<WizardProps, "onCancel" | "onComplete" | "contentStyle" | "stepperStyle"> & { busy: boolean };

const buttonStyle: CSSProperties = { borderRadius: "8px" };

export const DuplicateModuleWizard = ({ onCancel, busy, ...rest }: Props) => {
  const { createAccess } = useFetchModuleList();
  const formatAccessDenied = useFormatCreateModuleAccessDenied();
  const formatMessage = useFormatMessage(formatStrings);

  const { title, reset } = useDuplicateModuleSettings();

  const handleCancel = useCallback(() => {
    reset();
    onCancel();
  }, [onCancel, reset]);

  const errorMessage = createAccess?.denyCause ? formatAccessDenied(createAccess.denyCause) : undefined;

  return (
    <Wizard
      {...rest}
      errorMessage={errorMessage}
      pages={[
        {
          id: "module-details",
          content: <ModuleDetailsPage />,
          stepper: {
            back: <CancelButton style={buttonStyle} onClick={reset} />,
            next: (
              <CompleteButton
                style={buttonStyle}
                busy={busy}
                label={formatMessage(busy ? "duplicating" : "duplicate")}
                loadingProps={{ fillColor: colors.uiGrey["400"] }}
              />
            ),
            useBackAsCancel: true,
            advanceCondition: () => !!title && !!createAccess?.granted,
          },
        },
      ]}
      slotProps={{
        paper: { sx: { borderRadius: "14px" } },
      }}
      contentStyle={{ height: "300px" }}
      stepperStyle={{ padding: "0 45px 52px 45px" }}
      onCancel={handleCancel}
    />
  );
};

const formatStrings = {
  duplicate: {
    en: "Duplicate Module",
  },
  duplicating: {
    en: "Duplicating...",
  },
  next: {
    en: "Next",
  },
} satisfies FormatMessages;
